import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { CatalogTable } from '@/routes/DataCatalog/CatalogTable'
import CatalogFilterBar, { DATE_RANGE_DAYS } from '@/routes/DataCatalog/DataCatalogV2/CatalogFilterBar'
import { uploadFileWithProgress } from '@/service/uploadFileWithProgress'
import { apiService } from '@/service/api'
import type { DocumentRecord } from '@/types/catalog'
import { getDocumentStatus } from '@/utils/documentStatus'
import { DocumentStatus } from '@/components/StatusTag'
import { useAppStore } from '@/store'
import {
  extensionsToAcceptString,
  getAcceptedFileTypes,
  isFileTooLarge,
  MAX_FILE_SIZE_MB,
} from '@/utils/fileValidation'
import { useTranslation } from '@/i18n'
import { cn } from '@/lib/utils'
import { DIALOG_WIDTH } from '@/components/ui/dialogSize'

type FilesTab = 'upload' | 'catalog'

const FILTER_ALL = 'all'
// The board's DocumentsModal also offers a `pnid_set` filter (grouped P&ID
// sets). Deliberately omitted here: workflow picks attach per FILE, so sets
// render as their individual sheets and a set-level filter would only show
// group rows this dialog can't attach.
const FILTER_TYPES = ['document', 'transcript', 'pnid', 'pfd', 'timeseries'] as const
const DEFAULT_SORT = [{ id: 'created_at', desc: true }]
const SEARCH_DEBOUNCE_MS = 300
// Infinite-scroll page size (mirrors the board's Add Files modal — FS-534).
const PAGE_SIZE = 30

// Everything the Upload tab sends is a `document`. A workflow reads what it
// attaches as prose, so routing an attachment into the P&ID or timeseries
// pipeline instead would be the wrong promise — and the formats it accepts are
// therefore the document pipeline's own, taken from the app-wide list rather
// than restated here.
const UPLOAD_FILE_TYPE = 'document'
const UPLOAD_EXTENSIONS = getAcceptedFileTypes(UPLOAD_FILE_TYPE)
const UPLOAD_ACCEPT = extensionsToAcceptString(UPLOAD_EXTENSIONS)

/**
 * "Select files" modal (Figma "8. Edge Cases" — Overlay): Upload tab with a
 * drag-and-drop zone (REAL uploads through the Data Catalog's own uploader —
 * one multipart POST /v2/files/upload per file, processed into the catalog
 * asynchronously); Data Catalog tab is the SAME picker the
 * board's Add Files modal uses — CatalogFilterBar + CatalogTable with
 * multi-select, sorting, and infinite scroll — trimmed of board-only
 * concerns (graph-id swaps, canvas positioning). Emits {name, documentId}
 * per confirmed pick.
 */
export default function SelectFilesDialog({
  open,
  onClose,
  onSelect,
  preselectedFiles,
  lockedType,
  onUseAsMethod,
}: {
  open: boolean
  onClose: () => void
  /** Called once per confirmed selection with display names (+ catalog ids when picked from the catalog). */
  onSelect: (files: Array<{ name: string; documentId?: string }>) => void
  /**
   * Catalog docs already attached — pre-checked on open so the modal
   * round-trips ADD *and* REMOVE (mirrors the board Add-files modal).
   * Confirm then emits the full resulting selection. Passing name+id (not just
   * ids) lets confirm re-emit a pre-selected doc even if it isn't on the
   * currently-loaded catalog page.
   */
  preselectedFiles?: Array<{ name: string; documentId: string }>
  /**
   * Restrict the picker to ONE catalog type and drop the Upload tab.
   *
   * Used by the "use a past interview" intake route: the whole point is to pick an
   * EXISTING transcript, so an upload tab there would offer a file that is not an
   * interview, and the all-types list would bury the transcripts among P&IDs.
   */
  lockedType?: (typeof FILTER_TYPES)[number]
  /**
   * Offered ONLY while a run is parked asking for a method. Set, the Upload tab stops assuming
   * the file is durable knowledge and asks which of the two very different things the user
   * means — because they are reached by the same gesture and take wildly different times:
   *
   *   attach  → TUS + catalog + processing (minutes; usable by every future run)
   *   method  → text read once, no catalog row (seconds; answers the question in front of them)
   *
   * The handler is the SAME `supplyMethodDocument` the intake card uses, so this is a second
   * door to one capability rather than a second implementation of it.
   */
  onUseAsMethod?: (file: File) => unknown
}) {
  const { t } = useTranslation()
  const { allowedDocumentIds } = useAppStore()
  const [tab, setTab] = useState<FilesTab>(lockedType ? 'catalog' : 'upload')
  /** A file the user picked while a method ask is parked — held until they say what it is for. */
  const [pendingDecision, setPendingDecision] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // ── Catalog picker state (board Add Files parity) ─────────────────────────
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sorting, setSorting] = useState(DEFAULT_SORT)
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>(lockedType ?? FILTER_ALL)
  const [selectedUploader, setSelectedUploader] = useState(FILTER_ALL)
  const [selectedStatus, setSelectedStatus] = useState(FILTER_ALL)
  const [selectedCreationDate, setSelectedCreationDate] = useState(FILTER_ALL)
  const [organizationUsers, setOrganizationUsers] = useState<Array<[string, string]>>([])
  const [paginatedDocs, setPaginatedDocs] = useState<DocumentRecord[]>([])
  const [totalDocuments, setTotalDocuments] = useState(0)
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false)
  // The search string the CURRENT results were fetched for (null until the
  // first page lands) — see DocumentsModal's searchSettled for the rationale.
  const [loadedForSearch, setLoadedForSearch] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // The selection the dialog OPENED with (the already-attached docs) — used to
  // enable Confirm only on a real change and to detect removals on confirm.
  const [initialSelectedIds, setInitialSelectedIds] = useState<Set<string>>(new Set())
  const pageRef = useRef(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  // Every doc seen across pages, so cross-page selections resolve at Add time.
  const docsByIdRef = useRef<Map<string, DocumentRecord>>(new Map())

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset picker state when the dialog closes so each open starts clean.
  useEffect(() => {
    if (open) return
    setSelectedIds(new Set())
    setInitialSelectedIds(new Set())
    setSearchTerm('')
    setDebouncedSearch('')
    setSelectedTypeFilter(lockedType ?? FILTER_ALL)
    setSelectedUploader(FILTER_ALL)
    setSelectedStatus(FILTER_ALL)
    setSelectedCreationDate(FILTER_ALL)
    setPaginatedDocs([])
    setLoadedForSearch(null)
    setTotalDocuments(0)
    // An abandoned decision must not survive the close: reopening Add Files would otherwise
    // present the previously picked file again and let it be submitted without the user having
    // chosen anything this time (greptile P1 on #29851).
    setPendingDecision(null)
    pageRef.current = 0
    docsByIdRef.current = new Map()
  }, [open])

  // Seed the selection from the already-attached docs each time the dialog
  // OPENS, so they render pre-checked and removals/additions round-trip
  // (board Add-files parity). Keyed by the id set so a changed attachment list
  // re-seeds on the next open.
  const preselectedKey = (preselectedFiles ?? []).map((f) => f.documentId).join(',')
  useEffect(() => {
    if (!open) return
    const seed = new Set((preselectedFiles ?? []).map((f) => f.documentId))
    setSelectedIds(seed)
    setInitialSelectedIds(seed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preselectedKey])

  // Uploader-filter names (first catalog-tab open only).
  useEffect(() => {
    if (!open || tab !== 'catalog' || organizationUsers.length > 0) return
    let isMounted = true
    void apiService
      .getOrganizationUsers()
      .then((response) => {
        if (!isMounted || !response.users) return
        setOrganizationUsers(
          response.users
            .filter((user) => user.user_id)
            .map((user) => [user.user_id!, user.name || user.email || user.user_id!] as [string, string])
        )
      })
      .catch(() => {
        // Uploader filter just shows ids.
      })
    return () => {
      isMounted = false
    }
  }, [open, tab, organizationUsers.length])

  // One 0-indexed page; append accumulates for infinite scroll. Same request
  // shape as the board modal so the two pickers stay behaviorally identical.
  const fetchPage = useCallback(
    async (pageNum: number, append: boolean) => {
      abortControllerRef.current?.abort()
      const controller = new AbortController()
      abortControllerRef.current = controller
      if (append) setLoadingMore(true)
      else setIsLoadingDocuments(true)
      try {
        const sortField = sorting[0]
        const response = await apiService.listDocsPaginated(
          {
            page_number: pageNum,
            page_size: PAGE_SIZE,
            sort_by: sortField?.id === 'created_at' ? 'upload_started_at' : sortField?.id,
            sort_direction: sortField?.desc ? 'desc' : 'asc',
            search: debouncedSearch || undefined,
            // `lockedType` wins over the control: the fetch is the last line of defence for a
            // picker whose whole contract is "only this type".
            type: lockedType ?? (selectedTypeFilter !== FILTER_ALL ? selectedTypeFilter : undefined),
            user_id: selectedUploader !== FILTER_ALL ? selectedUploader : undefined,
            status_bucket: selectedStatus !== FILTER_ALL ? selectedStatus : undefined,
            created_after:
              selectedCreationDate !== FILTER_ALL && DATE_RANGE_DAYS[selectedCreationDate]
                ? new Date(
                    Date.now() - DATE_RANGE_DAYS[selectedCreationDate] * 86_400_000
                  ).toISOString()
                : undefined,
          },
          controller.signal
        )
        if (!controller.signal.aborted) {
          for (const doc of response.documents) docsByIdRef.current.set(doc.id, doc)
          setPaginatedDocs((prev) => (append ? [...prev, ...response.documents] : response.documents))
          setTotalDocuments(response.total)
        }
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return
        toast.error(t('workflow:files.load_failed', { defaultValue: 'Could not load catalog files' }))
      } finally {
        if (!controller.signal.aborted) {
          // Settle in finally (success OR error) so a failed fetch can't
          // leave the loading overlay stuck; a superseded fetch is aborted.
          setLoadedForSearch(debouncedSearch)
          setIsLoadingDocuments(false)
          setLoadingMore(false)
        }
      }
    },
    [sorting, debouncedSearch, selectedTypeFilter, selectedUploader, selectedStatus, selectedCreationDate, t]
  )

  // Reload from page 0 when the catalog tab opens or a filter/sort changes.
  useEffect(() => {
    if (!open || tab !== 'catalog') return
    pageRef.current = 0
    void fetchPage(0, false)
    return () => abortControllerRef.current?.abort()
  }, [open, tab, fetchPage])

  const hasMore = paginatedDocs.length < totalDocuments

  // The rendered results match what the user typed only when the typed
  // search, its debounced copy, and the search the results were fetched for
  // all agree. Anything else is a pending search → loading visual.
  const searchSettled = searchTerm.trim() === debouncedSearch && debouncedSearch === loadedForSearch
  const loadMore = useCallback(() => {
    if (isLoadingDocuments || loadingMore || !hasMore) return
    pageRef.current += 1
    void fetchPage(pageRef.current, true)
  }, [isLoadingDocuments, loadingMore, hasMore, fetchPage])

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore()
      },
      { root: scrollContainerRef.current, rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [loadMore, tab])

  // Permission scoping mirrors the board picker (annotator/embed allowlists).
  const tableData = useMemo(
    () =>
      allowedDocumentIds !== null
        ? paginatedDocs.filter((doc) => allowedDocumentIds.includes(doc.id))
        : paginatedDocs,
    [paginatedDocs, allowedDocumentIds]
  )
  // Workflow picks attach per FILE, so sets render as their sheets (no group rows).
  const orderedEntries = useMemo(
    () => tableData.map((doc) => ({ type: 'individual' as const, data: doc })),
    [tableData]
  )
  const disabledDocumentIds = useMemo(
    () => tableData.filter((doc) => getDocumentStatus(doc) === DocumentStatus.processing).map((doc) => doc.id),
    [tableData]
  )

  const handleRowSelect = useCallback((id: string, checked: string | boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const doc of tableData) {
          if (disabledDocumentIds.includes(doc.id)) continue
          if (checked) next.add(doc.id)
          else next.delete(doc.id)
        }
        return next
      })
    },
    [tableData, disabledDocumentIds]
  )

  // Names for pre-selected docs, so confirm can re-emit one that never loaded
  // onto the current catalog page.
  const preselectedNameById = useMemo(
    () => new Map((preselectedFiles ?? []).map((f) => [f.documentId, f.name])),
    [preselectedFiles]
  )
  // The confirmed selection differs from what we opened with (a real add or
  // remove) — otherwise Confirm is a no-op and stays disabled.
  const selectionChanged = useMemo(() => {
    if (selectedIds.size !== initialSelectedIds.size) return true
    for (const id of selectedIds) if (!initialSelectedIds.has(id)) return true
    return false
  }, [selectedIds, initialSelectedIds])

  const confirmCatalogSelection = () => {
    // Emit the FULL resulting selection (add + remove round-trip), resolving
    // each id from the loaded catalog or the pre-selected name fallback.
    const files = [...selectedIds]
      .map((id) => {
        const doc = docsByIdRef.current.get(id)
        if (doc) return { name: doc.name, documentId: doc.id }
        const name = preselectedNameById.get(id)
        return name ? { name, documentId: id } : null
      })
      .filter((f): f is { name: string; documentId: string } => Boolean(f))
    onSelect(files)
    onClose()
  }

  // Compact column set for THIS dialog (design parity): name, type, and who
  // uploaded it. The board's full catalog table adds size/status/date — here
  // they'd force a giant dialog; processing rows still disable via row data.
  const documentsColumns = useMemo(
    () => [
      { id: 'name', label: t('data_catalog:file_name', { defaultValue: 'File name' }) },
      { id: 'type', label: t('data_catalog:type', { defaultValue: 'Type' }) },
      { id: 'uploaded_by', label: t('data_catalog:uploaded_by', { defaultValue: 'Uploaded by' }) },
    ],
    [t]
  )
  const filterTypes = useMemo(() => [FILTER_ALL, ...FILTER_TYPES], [])
  const getDocTypeLabel = useCallback(
    // Dynamic i18n key → cast per the project's typed-i18next convention.
    (type: string): string => t(`data_catalog:doc_types.${type}` as never),
    [t]
  )

  // ── Upload tab ────────────────────────────────────────────────────────────
  //
  // Uploads run through the Data Catalog's OWN uploader: one multipart
  // `POST /v2/files/upload` per file, which is the route the catalog has always
  // used. It replaces an Uppy + TUS stack that could only work on a developer
  // laptop — TUS answers the create with a `Location` built from the path
  // NestJS sees, the edge strips `/v2` on the way in, and nothing put it back,
  // so every deployed environment was sent to `/api/…` and got a 404 there.
  // One request with no server-minted follow-up URL has nothing for a rewrite
  // to break.
  //
  // Files whose upload really produced a catalog row, by name. A 2xx alone is
  // not that: without an id there is nothing to attach, so listing it would
  // promise the user something the catalog will never have.
  //
  // Keyed by the name the file was QUEUED under, valued by what the catalog
  // actually stored. The two differ: `auto_rename` is on, so a second
  // `report.pdf` lands as `report (1).pdf`, and attaching the local name would
  // label the row with a name it does not have. The KEY stays local because
  // that is what the re-queue guard below deletes by.
  const linkedRef = useRef<Map<string, { documentId: string; name: string }>>(new Map())
  const onUploadComplete = useCallback(
    (file: { id: string; originalFilename: string; queuedFilename: string }) => {
      linkedRef.current.set(file.queuedFilename, {
        documentId: file.id,
        name: file.originalFilename,
      })
      // Attach HERE, per file, as its own upload lands — never from whatever the batch loop
      // returns. A batch-level emission is what attached nothing before: it ran while the
      // per-file work was still in flight, with an empty map (Khang on #29840).
      //
      // Emits the FULL resulting selection each time, because that is the contract
      // `handleFilesSelected` expects: it replaces the catalog entries with what it is given, so
      // a per-file emission would drop everything already attached.
      onSelect([
        ...(preselectedFiles ?? []),
        ...[...linkedRef.current.values()].map(({ name, documentId }) => ({ name, documentId })),
      ])
      toast.success(
        t('workflow:files.upload_complete', {
          name: file.originalFilename,
          defaultValue: '"{{name}}" uploaded — processing into the catalog',
        })
      )
    },
    [t, onSelect, preselectedFiles]
  )
  const onUploadError = useCallback(
    (_error: Error, fileName: string) =>
      toast.error(
        t('workflow:files.upload_failed', { name: fileName, defaultValue: 'Upload failed for "{{name}}"' })
      ),
    [t]
  )

  // Read through refs rather than captured when the upload is queued. The
  // dialog closes the moment it queues, and `onUploadComplete` changes identity
  // whenever `preselectedFiles` does — which is exactly when a file attaches —
  // so a captured copy would emit a stale selection (the same trap that made
  // attaching a file tear down the old Uppy instance mid-upload, UI-01).
  const onUploadCompleteRef = useRef(onUploadComplete)
  onUploadCompleteRef.current = onUploadComplete
  const onUploadErrorRef = useRef(onUploadError)
  onUploadErrorRef.current = onUploadError

  /**
   * Entry point for a pick or a drop. Its only job is to decide whether the intention is
   * ambiguous; the upload itself is `queueUploads`, so the "Attach to the workflow" button can
   * commit to that path without being asked the same question again.
   */
  const addUploads = (files: FileList | null) => {
    if (!files || files.length === 0) return
    // A parked method ask makes the gesture ambiguous — ask before spending minutes on a
    // pipeline the user may not have wanted. One file only: "use as the method" reads one
    // document, and a multi-select there would silently use the first.
    if (onUseAsMethod && files.length === 1) {
      setPendingDecision(files[0])
      return
    }
    queueUploads(Array.from(files))
  }

  // Takes a plain array, not a FileList: the decision card commits ONE already-picked file, and
  // rebuilding a FileList for it needs `DataTransfer`, which jsdom does not implement.
  const queueUploads = (files: File[]) => {
    if (files.length === 0) return

    const accepted: File[] = []
    for (const file of files) {
      // The restriction Uppy used to enforce. It has to live here now, and it
      // has to run on DROPPED files too — the input's `accept` never sees those.
      const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
      if (!UPLOAD_EXTENSIONS.includes(extension)) {
        toast.error(
          t('data_catalog:unsupported_file_type', {
            supportedTypes: UPLOAD_ACCEPT,
            defaultValue: 'This file type is not supported. Supported formats: {{supportedTypes}}',
          })
        )
        continue
      }
      if (isFileTooLarge(file)) {
        toast.error(
          t('data_catalog:add_data.file_exceeds_limit', {
            fileName: file.name,
            maxSize: MAX_FILE_SIZE_MB,
            defaultValue: '{{fileName}}: exceeds {{maxSize}}MB limit',
          })
        )
        continue
      }
      // Forget any earlier link for this NAME before uploading it again. The map is keyed by
      // filename and lives as long as the dialog, so uploading "a.pdf" successfully and then
      // uploading a different "a.pdf" that FAILS would otherwise still pass the linked-check
      // below — attaching the first file's id to the second file's name (greptile P1 on #29840).
      linkedRef.current.delete(file.name)
      accepted.push(file)
    }
    if (accepted.length === 0) return

    // Fire and forget: the attachment is emitted by `onUploadComplete`, once the file really
    // exists in the catalog. Nothing here waits, because the dialog closes below and the
    // uploads outlive it — failures surface as toasts through `onUploadError`.
    for (const file of accepted) {
      // No progress consumer: this dialog closes on queue, so the per-file bar the catalog
      // modal draws has nowhere to go.
      void uploadFileWithProgress(file, UPLOAD_FILE_TYPE, () => {})
        .then((response) => {
          // A 2xx with no id is not a usable upload: the id IS the catalog row the workflow
          // attaches, so there is nothing to attach and this counts as a failure.
          if (!response?.id) {
            throw new Error('The upload returned no catalog id')
          }
          onUploadCompleteRef.current({
            id: response.id,
            // The server's final name when it sent one, the local name otherwise.
            originalFilename: response.originalFilename ?? file.name,
            queuedFilename: file.name,
          })
        })
        .catch((error: unknown) => {
          onUploadErrorRef.current(
            error instanceof Error ? error : new Error(String(error)),
            file.name
          )
        })
    }
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose() }}>
      <DialogContent
        // ONE size for both tabs — switching Upload ↔ Data Catalog must not
        // jump the dialog (the catalog table runs a compact column set to fit).
        className={cn(DIALOG_WIDTH.md, 'gap-0 rounded-xl p-0')}
      >
        <DialogTitle className="sr-only">
          {t('workflow:files.title', { defaultValue: 'Select files' })}
        </DialogTitle>
        {/* Tabs header. min-w-0: DialogContent is a GRID — grid items default to
            min-width:auto, so a long unbreakable file name below would otherwise
            propagate its intrinsic width up and blow the dialog open. */}
        <div className="flex min-w-0 items-center gap-5 border-b border-border px-4 pt-3">
          {(
            [
              // A locked type is an "pick an existing X" flow — uploading a new file
              // cannot satisfy it, so the tab is not offered rather than offered-and-wrong.
              ...(lockedType
                ? []
                : ([['upload', t('workflow:files.tab_upload', { defaultValue: 'Upload' })]] as const)),
              ['catalog', t('workflow:files.tab_catalog', { defaultValue: 'Data Catalog' })],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={cn(
                'border-b-2 pb-2 text-sm font-medium transition-colors',
                tab === value
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/*
          UI-24 (Pegah): "Why different height and width for the two tabs?"
          The panes had set their own heights — the dropzone 210px, the catalog
          320px and up to 70vh — so switching tabs resized the dialog under the
          reader by at least 110px. The height now lives here, once, and each
          pane fills it.
        */}
        {/* UI-24 (Pegah): "Why different height and width for the two tabs?"
            A FIXED height, not a minimum. A floor only stops the panes being
            too short — the catalog pane still grew with its rows up to its own
            70vh cap, so the dialog was one size on Upload File and a taller one
            on Data Catalog, which is the resize being reported. With a fixed
            box both tabs render identically and the catalog scrolls inside it.
            max-h keeps it honest on a short viewport. */}
        <div className="flex h-[440px] max-h-[70vh] min-w-0 flex-col overflow-hidden p-4">
          {tab === 'upload' && pendingDecision ? (
            <div className="flex w-full flex-1 flex-col justify-center gap-3 rounded-lg border border-border p-4">
              <p className="text-sm font-medium text-foreground">
                {t('workflow:files.decision_title', {
                  name: pendingDecision.name,
                  defaultValue: 'How should "{{name}}" be used?',
                })}
              </p>
              <button
                type="button"
                className="rounded-lg border border-border px-3 py-2 text-left hover:border-foreground/40"
                onClick={() => {
                  const file = pendingDecision
                  setPendingDecision(null)
                  void onUseAsMethod?.(file)
                  onClose()
                }}
              >
                <span className="block text-sm font-medium text-foreground">
                  {t('workflow:files.decision_method_title', {
                    defaultValue: 'Use as the method now',
                  })}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {t('workflow:files.decision_method_hint', {
                    defaultValue:
                      'Read once to answer what the run is waiting on — seconds, and it is not added to the catalog.',
                  })}
                </span>
              </button>
              <button
                type="button"
                className="rounded-lg border border-border px-3 py-2 text-left hover:border-foreground/40"
                onClick={() => {
                  const file = pendingDecision
                  setPendingDecision(null)
                  queueUploads([file])
                }}
              >
                <span className="block text-sm font-medium text-foreground">
                  {t('workflow:files.decision_attach_title', {
                    defaultValue: 'Attach to the workflow',
                  })}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {t('workflow:files.decision_attach_hint', {
                    defaultValue:
                      'Added to the Data Catalog and available to every run of this workflow. Processing can take a few minutes.',
                  })}
                </span>
              </button>
            </div>
          ) : tab === 'upload' ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragOver(false)
                addUploads(e.dataTransfer.files)
              }}
              className={cn(
                // `flex-1` rather than min-h-[210px] (UI-24): the height lives
                // once on the shared container so switching tabs cannot resize
                // the dialog. Each pane fills whatever that box gives it.
                'flex w-full flex-1 flex-col items-center justify-center gap-3 rounded-lg border border-dashed transition-colors',
                // UI-31 (Pegah): "Where did this light blue color come from???"
                // It was cyan-400/60 idle and cyan-500 on drag — a colour the
                // app uses nowhere else.
                //
                // Putra settled what it should be instead: "It shouldn't show
                // when first showed up, when about to drop the files" — so the
                // accent is a DRAG state, not the resting look. At rest this is
                // the base `border` token, which is what the two buttons
                // directly above it use, and what the Data Catalog dropzone
                // uses — so it follows a theme change rather than pinning a hex.
                dragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-foreground/40'
              )}
            >
              <span className="grid size-10 place-items-center rounded-lg border border-border bg-background shadow-sm dark:bg-dark-card">
                <Upload className="size-4 text-foreground" />
              </span>
              <span className="text-sm font-medium text-foreground">
                {t('workflow:files.dropzone_title', {
                  defaultValue: 'Drag and drop files here, or click to select files',
                })}
              </span>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={UPLOAD_ACCEPT}
                className="hidden"
                onChange={(e) => addUploads(e.target.files)}
              />
            </button>
          ) : (
            /* Board Add Files parity: pinned filter bar, scrolling table body,
               pinned footer (FS-534 layout contract). */
            /* No height of its own: the container owns it, and the table
               scrolls inside. Any pixel height here is what made the two tabs
               diverge. */
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="shrink-0">
                <CatalogFilterBar
                  // The dialog's table container has no horizontal padding —
                  // align the search input flush with the table's left edge.
                  className="px-0"
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  selectedType={selectedTypeFilter}
                  // A locked picker cannot let the type be changed: this is "pick an existing
                  // transcript", and a P&ID or an ordinary document chosen here would be supplied
                  // to the run as an INTERVIEW (greptile P1 on #29842).
                  onTypeChange={lockedType ? () => {} : setSelectedTypeFilter}
                  filterTypes={filterTypes}
                  getDocTypeLabel={getDocTypeLabel}
                  selectedUploader={selectedUploader}
                  onUploaderChange={setSelectedUploader}
                  uploaders={organizationUsers}
                  selectedStatus={selectedStatus}
                  onStatusChange={setSelectedStatus}
                  selectedCreationDate={selectedCreationDate}
                  onCreationDateChange={setSelectedCreationDate}
                  onUploadClick={() => {}}
                  isUploadEnabled={false}
                />
              </div>
              <div
                ref={scrollContainerRef}
                className="relative min-h-0 flex-1 overflow-y-auto rounded-lg border"
              >
                {/* A typed search counts as loading from the FIRST keystroke:
                    the debounce window shows the same "Loading…" overlay as
                    the fetch, so the empty message never flashes mid-search. */}
                {(isLoadingDocuments || !searchSettled) && (
                  <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-white/80 dark:bg-dark-card/80">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <Loader2 className="size-5 animate-spin" />
                      <span>{t('common:loading', { defaultValue: 'Loading…' })}</span>
                    </div>
                  </div>
                )}
                <CatalogTable
                  cols={documentsColumns}
                  groupedEntries={[]}
                  individualData={tableData}
                  orderedEntries={orderedEntries}
                  // The overlay above is the loading visual — the empty
                  // message may only render once loading AND the search
                  // debounce have settled and a page has actually arrived.
                  showEmptyMessage={!isLoadingDocuments && searchSettled}
                  selectable
                  stickyHeader
                  selectedRows={[...selectedIds]}
                  onRowSelect={handleRowSelect}
                  onSelectAll={handleSelectAll}
                  defaultSort={sorting}
                  onSortChange={setSorting}
                  disabledRows={disabledDocumentIds}
                />
                {hasMore && <div ref={sentinelRef} aria-hidden className="h-px w-full" />}
                {loadingMore && (
                  <div className="flex items-center justify-center gap-2 py-3 text-sm text-gray-500 dark:text-gray-400">
                    <Loader2 className="size-4 animate-spin" />
                    <span>{t('common:loading', { defaultValue: 'Loading…' })}</span>
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center justify-end gap-2 pt-3">
                <IntuiButton variant="outline" size="sm" onClick={onClose}>
                  {t('common:buttons.cancel', { defaultValue: 'Cancel' })}
                </IntuiButton>
                <IntuiButton
                  size="sm"
                  // Enabled on any add/remove vs the opened selection — so a
                  // pure removal (fewer selected) confirms, not just additions.
                  disabled={!selectionChanged}
                  onClick={confirmCatalogSelection}
                >
                  {/* Count is the resulting attachment total after confirm. */}
                  {t('workflow:files.add_selected', {
                    count: selectedIds.size,
                    defaultValue: `Attach ${selectedIds.size} file${selectedIds.size === 1 ? '' : 's'}`,
                  })}
                </IntuiButton>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
